import { For, Show, createSignal } from "solid-js";

import { SelectionActionBar } from "~/components/SelectionActionBar";
import { useArchiveProduct, useUpdateProduct } from "~/lib/hooks";
import type { ProductListItem } from "~/lib/types";

type Props = {
  wsKey: () => string;
  count: () => number;
  rows: () => ProductListItem[];
  onClear: () => void;
};

type Mode = "archive" | "activate" | "deactivate";

type RowOutcome = { id: string; name: string; ok: boolean; message?: string };

export function ProductsBulkBar(props: Props) {
  const archiveMutation = useArchiveProduct(props.wsKey);
  const updateMutation = useUpdateProduct(props.wsKey);

  const [mode, setMode] = createSignal<Mode | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [outcome, setOutcome] = createSignal<RowOutcome[] | null>(null);

  const reset = () => {
    setMode(null);
    setOutcome(null);
  };
  const close = () => reset();

  const runArchive = async () => {
    setBusy(true);
    const results: RowOutcome[] = [];
    for (const r of props.rows()) {
      try {
        await archiveMutation.mutateAsync({ productId: r.id, version: r.version });
        results.push({ id: r.id, name: r.name, ok: true });
      } catch (err) {
        results.push({
          id: r.id,
          name: r.name,
          ok: false,
          message: (err as Error).message,
        });
      }
    }
    setOutcome(results);
    setBusy(false);
  };

  const runSetActive = async (active: boolean) => {
    setBusy(true);
    const results: RowOutcome[] = [];
    for (const r of props.rows()) {
      // Skip rows already in the target state — saves API calls and avoids
      // bumping `version` for no actual change.
      if (r.active === active) {
        results.push({ id: r.id, name: r.name, ok: true });
        continue;
      }
      try {
        await updateMutation.mutateAsync({
          productId: r.id,
          version: r.version,
          body: { active },
        });
        results.push({ id: r.id, name: r.name, ok: true });
      } catch (err) {
        results.push({
          id: r.id,
          name: r.name,
          ok: false,
          message: (err as Error).message,
        });
      }
    }
    setOutcome(results);
    setBusy(false);
  };

  const okCount = () => outcome()?.filter((r) => r.ok).length ?? 0;
  const errCount = () => outcome()?.filter((r) => !r.ok).length ?? 0;

  return (
    <>
      <SelectionActionBar count={props.count} onClear={props.onClear}>
        <button type="button" class="btn btn-sm" onClick={() => setMode("activate")}>
          Activate
        </button>
        <button type="button" class="btn btn-sm" onClick={() => setMode("deactivate")}>
          Deactivate
        </button>
        <button type="button" class="btn btn-sm btn-warning" onClick={() => setMode("archive")}>
          Archive
        </button>
      </SelectionActionBar>

      <Show when={mode()}>
        <dialog
          open
          class="modal modal-open"
          aria-modal="true"
          aria-labelledby="products-bulk-title"
        >
          <div class="modal-box max-w-xl">
            <h3 id="products-bulk-title" class="font-bold text-lg mb-3">
              <Show when={mode() === "archive"}>
                Archive {props.count()} product{props.count() === 1 ? "" : "s"}?
              </Show>
              <Show when={mode() === "activate"}>
                Activate {props.count()} product{props.count() === 1 ? "" : "s"}?
              </Show>
              <Show when={mode() === "deactivate"}>
                Deactivate {props.count()} product{props.count() === 1 ? "" : "s"}?
              </Show>
            </h3>

            <Show when={!outcome()}>
              <Show when={mode() === "archive"}>
                <p class="text-sm opacity-80 mb-4">
                  Archived products are hidden from pickers and lists; existing line items keep
                  their snapshot. Reverse per-product from the full-list view with "Include
                  archived".
                </p>
              </Show>
              <Show when={mode() === "activate"}>
                <p class="text-sm opacity-80 mb-4">
                  Active products appear in the line-item product picker. Rows already active are
                  skipped.
                </p>
              </Show>
              <Show when={mode() === "deactivate"}>
                <p class="text-sm opacity-80 mb-4">
                  Deactivated products are hidden from the picker but stay in the catalogue.
                  Existing line items are unaffected.
                </p>
              </Show>
            </Show>

            <Show when={outcome()}>
              {(rows) => (
                <div class="mb-4">
                  <div class="stats stats-horizontal w-full text-sm shadow-sm">
                    <div class="stat py-2">
                      <div class="stat-title">Total</div>
                      <div class="stat-value text-lg">{rows().length}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Succeeded</div>
                      <div class="stat-value text-lg text-success">{okCount()}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Failed</div>
                      <div class="stat-value text-lg text-error">{errCount()}</div>
                    </div>
                  </div>
                  <Show when={errCount() > 0}>
                    <div
                      class="mt-3 max-h-64 overflow-y-auto bg-base-200 rounded-lg p-2"
                      aria-label="Failed rows"
                    >
                      <ul class="text-xs space-y-1">
                        <For each={rows().filter((r) => !r.ok)}>
                          {(r) => (
                            <li>
                              <strong>{r.name}</strong>: {r.message ?? "Failed"}
                            </li>
                          )}
                        </For>
                      </ul>
                    </div>
                  </Show>
                </div>
              )}
            </Show>

            <div class="modal-action">
              <button type="button" class="btn btn-ghost" onClick={close} disabled={busy()}>
                {outcome() ? "Close" : "Cancel"}
              </button>
              <Show when={!outcome()}>
                <Show when={mode() === "archive"}>
                  <button
                    type="button"
                    class="btn btn-warning"
                    onClick={runArchive}
                    disabled={busy()}
                  >
                    {busy() ? "Archiving…" : "Archive"}
                  </button>
                </Show>
                <Show when={mode() === "activate"}>
                  <button
                    type="button"
                    class="btn btn-primary"
                    onClick={() => runSetActive(true)}
                    disabled={busy()}
                  >
                    {busy() ? "Activating…" : "Activate"}
                  </button>
                </Show>
                <Show when={mode() === "deactivate"}>
                  <button
                    type="button"
                    class="btn btn-primary"
                    onClick={() => runSetActive(false)}
                    disabled={busy()}
                  >
                    {busy() ? "Deactivating…" : "Deactivate"}
                  </button>
                </Show>
              </Show>
            </div>
          </div>
        </dialog>
      </Show>
    </>
  );
}
